import posthog from 'posthog-js';
import { shouldSuppressAnalyticsEvent } from './analyticsNoise';
function hasActiveUser(user) {
    if (!user) {
        return false;
    }
    return !!String(user.id ?? '').trim();
}
function getCurrentUrl(captureResult) {
    const currentUrl = captureResult.properties?.$current_url;
    if (typeof currentUrl === 'string' && currentUrl) {
        return currentUrl;
    }
    if (typeof window !== 'undefined') {
        return window.location.href;
    }
    return undefined;
}
function getStringProperty(properties, key) {
    const value = properties?.[key];
    return typeof value === 'string' && value ? value : undefined;
}
function routeMatchesPath(routeName, urlPath) {
    const escapedRoute = routeName
        .split('/')
        .map((segment) => (segment.startsWith(':') ? '[^/]+' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
        .join('/');
    return new RegExp(`^${escapedRoute}/?$`).test(urlPath);
}
function getUserRoleValue(user, metadata) {
    const candidate = user?.userRole ?? user?.role ?? user?.userRoles ?? user?.roles ?? metadata.userRole ?? metadata.userRoles;
    return normalizeValue(candidate);
}
function buildCompatibilityProperties(info, metadata, user, captureResult) {
    const url = getCurrentUrl(captureResult);
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://example.invalid';
    const urlObject = url ? new URL(url, baseOrigin) : null;
    const urlPath = urlObject?.pathname;
    const isPageview = captureResult.event === '$pageview';
    const capturedPageName = getStringProperty(captureResult.properties, 'pageName');
    const metadataPageName = typeof metadata.pageName === 'string' ? metadata.pageName : undefined;
    const pageName = isPageview ? capturedPageName ?? urlPath : metadataPageName;
    const capturedRouteName = getStringProperty(captureResult.properties, 'routeName');
    const metadataRouteName = typeof metadata.routeName === 'string' ? metadata.routeName : undefined;
    const routeName = capturedRouteName ??
        (urlPath && metadataRouteName && routeMatchesPath(metadataRouteName, urlPath) ? metadataRouteName : undefined);
    return {
        app_name: String(metadata.application ?? info.application ?? info.service),
        domain: String(metadata.domain ?? urlObject?.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')),
        event_type: captureResult.event,
        session_id: posthog.get_session_id(),
        rum_session_id: posthog.get_session_id(),
        user_id: user?.id,
        user_email: user?.email,
        user_name: user?.name ?? user?.username,
        user_role: getUserRoleValue(user, metadata),
        url,
        url_path: urlPath,
        url_path_group: routeName ?? (isPageview ? urlPath : undefined),
        view_id: posthog.getPageViewId(),
        view_name: pageName,
        browser: captureResult.properties?.$browser,
        device: captureResult.properties?.$device_type,
        geo: captureResult.properties?.$geoip_country_name ?? captureResult.properties?.$geoip_country_code,
    };
}
function composeBeforeSend(info, currentMetadata, getCurrentUser, beforeSend) {
    const beforeSendFns = Array.isArray(beforeSend) ? beforeSend : beforeSend ? [beforeSend] : [];
    return (captureResult) => {
        if (!captureResult) {
            return captureResult;
        }
        captureResult.properties = {
            ...captureResult.properties,
            ...buildCompatibilityProperties(info, currentMetadata, getCurrentUser(), captureResult),
        };
        let nextResult = captureResult;
        for (const beforeSendFn of beforeSendFns) {
            nextResult = beforeSendFn(nextResult);
            if (!nextResult) {
                return null;
            }
        }
        return nextResult;
    };
}
function normalizeError(error) {
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            stack: error.stack,
        };
    }
    return {
        message: typeof error === 'string' ? error : JSON.stringify(error),
    };
}
function normalizeValue(value) {
    if (value === undefined) {
        return undefined;
    }
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (value instanceof Error) {
        return normalizeError(value);
    }
    if (Array.isArray(value)) {
        const result = value
            .map((item) => normalizeValue(item))
            .filter((item) => item !== undefined);
        return result;
    }
    if (typeof value === 'object') {
        const result = {};
        for (const [key, child] of Object.entries(value)) {
            const normalized = normalizeValue(child);
            if (normalized !== undefined) {
                result[key] = normalized;
            }
        }
        return result;
    }
    return String(value);
}
export function normalizeProperties(metadata) {
    if (!metadata) {
        return undefined;
    }
    const result = {};
    for (const [key, value] of Object.entries(metadata)) {
        const normalized = normalizeValue(value);
        if (normalized !== undefined && normalized !== null) {
            result[key] = normalized;
        }
    }
    return Object.keys(result).length > 0 ? result : undefined;
}
function shouldDisableSessionRecording(config) {
    if (config.disableSessionRecording !== undefined) {
        return config.disableSessionRecording;
    }
    if (config.replaySampleRate !== undefined) {
        return config.replaySampleRate <= 0;
    }
    return false;
}
export function posthogReporter(info, config) {
    const manualPageviews = config.trackViewsManually ?? false;
    const currentMetadata = {};
    let currentUser = null;
    posthog.init(config.apiKey, {
        api_host: config.apiHost,
        autocapture: config.autocapture ?? true,
        before_send: composeBeforeSend(info, currentMetadata, () => currentUser, config.beforeSend),
        bootstrap: config.bootstrap,
        capture_pageleave: manualPageviews ? false : config.capturePageleave ?? 'if_capture_pageview',
        capture_pageview: manualPageviews ? false : config.capturePageview ?? 'history_change',
        cross_subdomain_cookie: config.crossSubdomainCookie ?? true,
        debug: config.debug ?? false,
        defaults: config.defaults ?? '2025-05-24',
        disable_session_recording: shouldDisableSessionRecording(config),
        loaded: config.loaded ?? (() => { }),
        persistence: config.persistence,
        persistence_name: config.persistenceName,
        person_profiles: config.personProfiles ?? 'identified_only',
        ...config.sdkConfig,
    });
    const reporter = {
        ...config,
        trackEvent: function (event) {
            if (shouldSuppressAnalyticsEvent(event.message, config.ignoreTrackEventPatterns)) {
                return;
            }
            posthog.capture(event.message, {
                message: event.message,
                level: event.level,
                ...normalizeProperties(event.metadata),
                ...normalizeProperties(event.tags),
                ...normalizeProperties(event.metrics),
            });
        },
        addBreadcrumb: function (breadcrumb) {
            posthog.capture(breadcrumb.message, {
                message: breadcrumb.message,
                category: breadcrumb.category,
                breadcrumb: true,
                ...normalizeProperties(breadcrumb.metadata),
            });
        },
        addMetadata: function (metadata) {
            const nextProperties = normalizeProperties(metadata);
            for (const [key, value] of Object.entries(metadata)) {
                if (value === null) {
                    delete currentMetadata[key];
                    posthog.unregister(key);
                }
                else {
                    currentMetadata[key] = value;
                }
            }
            if (!nextProperties) {
                return;
            }
            posthog.register(nextProperties);
        },
        setUser: function (user) {
            currentUser = user;
            if (!hasActiveUser(user)) {
                posthog.reset();
                const restoredMetadata = normalizeProperties(currentMetadata);
                if (restoredMetadata) {
                    posthog.register(restoredMetadata);
                }
                currentUser = null;
                return;
            }
            const { id, ...rest } = user;
            posthog.identify(String(id), normalizeProperties(rest));
            posthog.register({
                user_id: String(id),
                user_email: user.email,
                user_name: user.name ?? user.username,
                user_role: getUserRoleValue(user, currentMetadata),
            });
        },
        setRouteName: function (routeName) {
            reporter.addMetadata({ routeName });
        },
        setPageName: function (pageName) {
            reporter.addMetadata({ pageName });
            if (manualPageviews) {
                posthog.capture('$pageview', {
                    pageName,
                    routeName: typeof currentMetadata.routeName === 'string' ? currentMetadata.routeName : undefined,
                });
            }
        },
        reportError: function (error, metadata) {
            posthog.captureException(error, {
                ...normalizeProperties(metadata),
                ...normalizeError(error),
            });
        },
        reportFeatureFlag: function (flag) {
            reporter.addMetadata({
                [`featureFlag.${flag.name}`]: flag.value,
            });
        },
        recordSession: function () {
            posthog.startSessionRecording({
                event_trigger: true,
                linked_flag: true,
                sampling: true,
                url_trigger: true,
            });
        },
        recordSessionStop: function () {
            posthog.stopSessionRecording();
        },
    };
    return reporter;
}
//# sourceMappingURL=posthogReporter.js.map